import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import type {
    DurationInMinutes} from "@@/shared/date-time/time-spans";
import {
    durationInMinutesZodSchema,
} from "@@/shared/date-time/time-spans";
import { idZodSchema } from "@@/shared/id";
import { phoneNumberZodSchema } from "@@/shared/phone";
import { storageItemImageIdZodSchema } from "@@/shared/storage-items/storage-item-images/storage-item-image-id";
import { emptyObjectOf } from "@@/utilities/empty";
import { generateId } from "@@/utilities/generate-id";
import { z } from "zod";
import { weeklyScheduleZodSchema } from "../../weekly-schedules/weekly-schedule";
import { providerSlugZodSchema } from "../provider";
import type {
    ProviderId} from "../provider-id";
import {
    providerIdFactory,
    providerIdZodSchema,
} from "../provider-id";

const providerCreateCommandZodObject = z.object({
    _id: idZodSchema,
    _type: z.literal("PROVIDER_CREATE_COMMAND"),
    _createdAt: isoAndUnixZodSchema,
    providerId: providerIdZodSchema,
    name: z.string(),
    slug: providerSlugZodSchema,
    subtitle: z.string(),
    description: z.string().optional(),
    areas: z.record(z.string(), z.boolean()),
    publicEmail: z.string().email().optional(),
    orderEmail: z.string().email(),
    prepTime: durationInMinutesZodSchema,
    publicPhone: phoneNumberZodSchema.optional(),
    orderPhone: phoneNumberZodSchema,
    address: z.object({
        street: z.string(),
        city: z.string(),
        country: z.string(),
        zipCode: z.string(),
    }),
    businessHours: weeklyScheduleZodSchema,
    logoImage: storageItemImageIdZodSchema,
    posterImage: storageItemImageIdZodSchema,
});

type ProviderCreateCommand = z.infer<typeof providerCreateCommandZodObject>;

const providerCreateCommandFactoryParamsZodObject =
    providerCreateCommandZodObject.omit({
        _id: true,
        _type: true,
        _createdAt: true,
    });

type FactoryParams = z.infer<
    typeof providerCreateCommandFactoryParamsZodObject
>;

function providerCreateCommandFactory(
    input: FactoryParams,
): ProviderCreateCommand {
    // TODO! make sure data has been validated before reaching here
    const data = input as Required<FactoryParams>;
    const output: ProviderCreateCommand = {
        ...data,
        _id: generateId("add_provider_command_"),
        _type: "PROVIDER_CREATE_COMMAND",
        _createdAt: nowIsoAndUnix(),
    };
    return output;
}

const providerCreateCommandDraftFactory = (
    params?: Partial<FactoryParams> & {
        providerId: ProviderId;
    },
) => {
    const providerCreateCommandDraft: Partial<FactoryParams> = {
        areas: emptyObjectOf<Record<string, boolean>>(),
        prepTime: 20 as DurationInMinutes,
        address: {
            street: "",
            city: "",
            country: "",
            zipCode: "",
        },
        providerId: params?.providerId ?? providerIdFactory(),
        ...(params ?? {}),
    };
    return providerCreateCommandDraft;
};

// const providerCreateCommandZodSchema: z.ZodSchema<ProviderCreateCommand> =
//     z.object({
//         _id: idZodSchema,
//         _type: z.literal("PROVIDER_CREATE_COMMAND"),
//         _createdAt: isoAndUnixZodSchema,
//         name: z.string(),
//         prepTime: z.number().min(0) as unknown as z.ZodType<MINUTES>,
//         slug: z.string() as unknown as z.ZodType<ProviderSlug>,
//         subtitle: z.string(),
//         description: z.string().optional(),
//         providerId: providerIdZodSchema,
//         address: z.object({
//             street: z.string(),
//             city: z.string(),
//             country: z.string(),
//             zipCode: z.string(),
//         }),
//         areas: z.record(z.string(), z.boolean()),
//         businessHours: weeklyScheduleZodSchema,
//         category: z.string(),
//         publicEmail: z.string().email(),
//         orderEmail: z.string().email(),
//         logoImage: storageItemIdZodSchema,
//         posterImage: storageItemIdZodSchema,
//         publicPhone: z
//             .object({
//                 countryCode: z.string(),
//                 phoneNumber: phoneNumberZodSchema,
//             })
//             .optional(),
//         orderPhone: z.object({
//             countryCode: z.string(),
//             phoneNumber: phoneNumberZodSchema,
//         }),
//     }) as unknown as z.ZodSchema<ProviderCreateCommand>;

export {
    providerCreateCommandDraftFactory,
    providerCreateCommandFactory,
    providerCreateCommandFactoryParamsZodObject,
    providerCreateCommandZodObject,
};
export type {
    ProviderCreateCommand,
    FactoryParams as ProviderCreateCommandFactoryParams,
};
